<template>
  <v-navigation-drawer
    v-model="show"
    v-lock-scroll="show"
    app
    temporary
    right
    width="850px"
    class="est-cost-panel"
  >
    <close-panel-button
      @close="() => show = false"
      text="Close"
    />

    <v-container class="px-9 py-0" v-if="gift && show">
      <v-row class="flex-column">
        <v-col cols="12" class="est-cost-panel__title">
          Estimate deposit
        </v-col>
        <v-col cols="12" class="est-cost-panel__subtitle">
          The deposit is calculated based on the highest possible gift value, shipping, and tax,
          as the recipient’s selections and location are unknown. Any difference will be
          refunded after the gifts are redeemed
        </v-col>

        <v-col cols="5">
          <label for="recipients">Number of recipients</label>
          <common-input
            v-model.number="recipients"
            @input="recipientsHandler"
            type="number"
            :min="0"
            :step="1"
            id="recipients"
            name="recipients"
            class="pt-1"
          />
        </v-col>

        <v-col cols="5">
          <v-row dense class="calculated-prices">
            <common-loader v-if="loading" style="padding: 30px 0" />

            <template v-else>
              <!-- Recipients -->
              <v-col cols="7">Recipients</v-col>
              <v-col cols="5"> {{ recipients || 0 }} </v-col>
              <!-- Price -->
              <v-col cols="7">Max gift cost</v-col>
              <v-col cols="5">{{ giftMaxCost | priceFilter }}</v-col>
              <!-- Shipping cost -->
              <template v-if="giftMaxShipping > giftCardFeeTotal">
                <v-col cols="7">{{ giftMaxShippingLabel }}</v-col>
                <v-col cols="5">{{ giftMaxShipping | priceFilter }}</v-col>
              </template>
              <!-- Tax -->
              <template v-if="giftSalesTax">
                <v-col cols="7">Max sales tax</v-col>
                <v-col cols="5">{{ giftSalesTax | priceFilter }}</v-col>
              </template>
              <!-- eGift Fee -->
                <v-col cols="7">{{ egiftFeeLabel }}</v-col>
                <v-col cols="5">{{ egiftFeeValue }}</v-col>
              <!-- Dropship fee -->
              <template v-if="dropshipFeeTotal">
                <!-- total -->
                <v-col cols="7">Max Dropship fee</v-col>
                <v-col cols="5">{{ dropshipFeeTotal | priceFilter }}</v-col>
              </template>
              <!-- Gift Card Fee -->
              <template v-if="giftCardFeeTotal">
                <v-col cols="7">Gift card fee</v-col>
                <v-col cols="5">{{ giftCardFeeTotal | priceFilter }}</v-col>
              </template>
              <!-- Import charges -->
              <template v-if="giftMaxImportCharges">
                <v-col cols="7" class="d-flex align-center text-no-wrap">
                  Max import charges

                  <popper
                    :options="{
                      placement: 'top',
                      modifiers: { offset: { offset: '0, 5px' } },
                    }"
                  >
                    <div class="popper pa-4 text-left" style="min-width: 150px; max-width: 500px; white-space: normal;">
                      Import charges may apply to recipients located outside the US.
                    </div>
                    <div slot="reference" class="d-flex ml-2">
                      <icons-question width="20" height="20" class="pointer" />
                    </div>
                  </popper>
                </v-col>
                <v-col cols="5">{{ giftMaxImportCharges | priceFilter }}</v-col>
              </template>
              <!-- EST Cost -->
              <v-col cols="7">Deposit</v-col>
              <v-col cols="5">{{ giftTotalCost | priceFilter }}</v-col>
            </template>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </v-navigation-drawer>
</template>

<script>
import Api from '@/axios/api'
import debounce from 'lodash/debounce'

import panelVModel from '@/mixins/panelVModel'
import gift from './mixins/gift'

import Popper from 'vue-popperjs'

export default {
  name: 'TheEstCostPanel',
  components: {
    Popper
  },
  mixins: [
    panelVModel,
    gift
  ],
  data: () => ({
    loading: false,
    abortController: null,
    recipients: 1,
    campaignCosts: {
      fee: 0,
      fee_per_unit: 0,
      fee_rate: 0,
      fee_type: 'quantity',
      gift_card_fee: 0,
      gift_card_fee_per_unit: 0,
      gift_card_fee_rate: 0,
      shipping: 0,
      shipping_per_unit: 0,
      subtotal: 0,
      subtotal_per_unit: 0,
      dropship_fee: 0,
      dropship_fee_per_unit: 0,
      tax: 0,
      tax_rate: 0,
      total: 0,
      import_charges: 0,
      is_shipping_flat_rate: false,
      is_customer_created: false
    },
  }),
  computed: {
    egiftFeeLabel () {
      const { fee_rate, fee_type } = this.campaignCosts
      return fee_type === 'percent' ? `${fee_rate}% eGift fee` : 'eGift fee'
    },
    egiftFeeValue () {
      if (this.egiftFeeTotal === 0) {
        return 'Waived';
      }

      return this.$options.filters.priceFilter(this.egiftFeeTotal);
    },
    giftMaxCost () {
      return this.campaignCosts?.subtotal ?? 0
    },
    giftMaxShipping () {
      return this.campaignCosts?.shipping ?? 0
    },
    giftSalesTax () {
      return this.campaignCosts?.tax ?? 0
    },
    dropshipFeeTotal () {
      return this.campaignCosts?.dropship_fee ?? 0
    },
    egiftFeeTotal () {
      return this.campaignCosts?.fee ?? 0
    },
    giftCardFeeTotal () {
      return this.campaignCosts?.gift_card_fee ?? 0
    },
    giftTotalCost () {
      return this.campaignCosts?.total ?? 0
    },
    giftMaxShippingLabel() {
      const {
        is_customer_created = false,
        is_shipping_flat_rate = false,
      } = this.campaignCosts ?? {}

      if (is_customer_created) return 'Max shipping'
      if (is_shipping_flat_rate) return 'Flat rate shipping'
      return 'Estimated shipping'
    },
    giftMaxImportCharges () {
      return this.campaignCosts?.import_charges ?? 0
    },
  },
  watch: {
    show: function (val) {
      if (val) {
        this.calculatePrices()
      } else {
        Object.assign(this.$data, this.$options.data())
      }
    },
    recipients: 'handleRecipientsChange'
  },
  methods: {
    recipientsHandler () {
      if (this.recipients < 0) this.$nextTick(() => { this.recipients = 0 })
    },
    calculatePrices () {
      const {
        recipients,
        gift: { entity_id: id },
        abortController,
      } = this

      if (abortController) {
        abortController?.abort()
        this.abortController = null
      }

      this.loading = true
      this.abortController = new AbortController();

      Api.get(`campaigns/send-egift/${id}/calculate`, {
        signal: this.abortController.signal,
        params: { number_of_recipients: recipients || 1 }
      })
        .then((costs) => {
          this.campaignCosts = costs
          this.loading = false
        })
        .catch((e) => {
          if (e?.message !== 'canceled') {
            this.loading = false
          }
        })
        .finally(() => (this.abortController = null))
    },
    handleRecipientsChange: debounce(function () { this.calculatePrices() }, 400)
  }
}
</script>

<style lang="scss" scoped>
.est-cost-panel {
  &__title {
    font-family: 'Lato-Regular', sans-serif !important;
    line-height: 44px;
    font-size: 40px;
    color: #000;
  }

  &__subtitle {
    font-family: 'Lato-Italic', sans-serif !important;
    line-height: 18px;
    font-size: 16px;
    color: #979797;
  }

  label {
    font-family: 'Lato-Bold', sans-serif !important;
    line-height: 18px;
    font-size: 15px;
    color: #000;
  }

  .calculated-prices {
    background: #EDFCFB;
    padding: 8px 16px;

    .col {
      font-size: 15px;
      line-height: 24px;

      &:nth-child(odd) {
        font-family: 'Lato-Bold', sans-serif !important;
        color: #222325;
      }

      &:nth-child(even) {
        font-family: 'Lato-Regular', sans-serif !important;
        color: #000;
        text-align: right;
      }
    }
  }
}
</style>
